/**
 * レジストリ配布の対象指定(AG2)。ポリシー 1 件ごとに 全端末 / 動的グループ / 個別の端末。
 * 何も選んでいなければ配らない。グループは「端末の利用者がそのグループの今のメンバー」で当たる。
 */
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Checkbox, Form, Modal, Select, Space, Table, Tag, Typography } from "antd";
import { useMemo, useState } from "react";

import type { DeviceRegistryPolicy, RegistryTarget } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { reasonLabel, usersLabel } from "./constants";
import { errorText, useDevices, useDynGroups } from "./hooks";

/** 端末の検索つき複数選択。選んだ端末の名前は呼び出し側の `names` で引く。 */
export function DeviceSelect({
  value,
  onChange,
  names,
  onNames,
  multiple = true,
  placeholder,
  style,
}: {
  value: number[];
  onChange: (v: number[]) => void;
  names: Record<string, string>;
  onNames: (n: Record<string, string>) => void;
  multiple?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
}) {
  const [q, setQ] = useState("");
  const list = useDevices({ q, limit: 20 });
  const found = list.data?.items ?? [];
  const options = [
    ...value
      .filter((id) => !found.some((d) => d.id === id))
      .map((id) => ({ value: id, label: names[String(id)] ?? `#${id}` })),
    ...found.map((d) => ({
      value: d.id,
      label: `${d.hostname}${d.employee_name ? `(${d.employee_name})` : ""}`,
    })),
  ];
  const remember = (ids: number[]) => {
    const add: Record<string, string> = {};
    for (const d of found) if (ids.includes(d.id)) add[String(d.id)] = d.hostname;
    if (Object.keys(add).length) onNames(add);
  };
  return (
    <Select<number[] | number>
      mode={multiple ? "multiple" : undefined}
      allowClear
      showSearch
      filterOption={false}
      onSearch={setQ}
      loading={list.isFetching}
      value={multiple ? value : value[0]}
      onChange={(v) => {
        const ids = v === undefined || v === null ? [] : Array.isArray(v) ? v : [v];
        remember(ids);
        onChange(ids);
      }}
      options={options}
      placeholder={placeholder ?? "ホスト名・製造番号・利用者で探す"}
      style={style ?? { width: "100%" }}
      notFoundContent={list.isFetching ? "探している…" : "見つからない"}
    />
  );
}

/** 表の 1 セルに出す対象の要約。 */
export function TargetSummary({
  target,
  groupNames,
  deviceNames,
}: {
  target: RegistryTarget;
  groupNames: Record<string, string>;
  deviceNames: Record<string, string>;
}) {
  if (target.all) return <Tag color="blue">全端末</Tag>;
  if (!target.groups.length && !target.devices.length) return <Tag color="red">配らない</Tag>;
  return (
    <Space size={[0, 4]} wrap>
      {target.groups.map((g) => (
        <Tag key={`g${g}`} color="purple">
          {groupNames[String(g)] ?? `グループ #${g}`}
        </Tag>
      ))}
      {target.devices.map((d) => (
        <Tag key={`d${d}`}>{deviceNames[String(d)] ?? `端末 #${d}`}</Tag>
      ))}
    </Space>
  );
}

type TargetFieldsProps = {
  value: RegistryTarget;
  onChange: (t: RegistryTarget) => void;
  groupOptions: { value: number; label: string }[];
  deviceNames: Record<string, string>;
  onDeviceNames: (n: Record<string, string>) => void;
};

/** 対象の入力欄(全端末 / 動的グループ / 個別の端末)。ダイアログとひな形の両方で使う。 */
export function TargetFields({ value, onChange, groupOptions, deviceNames, onDeviceNames }: TargetFieldsProps) {
  return (
    <>
      <Form.Item>
        <Checkbox checked={value.all} onChange={(e) => onChange({ ...value, all: e.target.checked })}>
          全端末に配る
        </Checkbox>
      </Form.Item>
      <Form.Item
        label="動的グループ"
        extra="端末の利用者(台帳の利用者)がそのグループの今のメンバーなら当たる。利用者の無い端末には当たらない"
      >
        <Select<number[]>
          mode="multiple"
          allowClear
          disabled={value.all}
          value={value.groups}
          onChange={(v) => onChange({ ...value, groups: v })}
          options={groupOptions}
          optionFilterProp="label"
          placeholder="グループを選ぶ"
        />
      </Form.Item>
      <Form.Item label="個別の端末">
        <DeviceSelect
          value={value.devices}
          onChange={(v) => onChange({ ...value, devices: v })}
          names={deviceNames}
          onNames={onDeviceNames}
        />
      </Form.Item>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        3 つは「または」。何も選ばないと配らない。対象から外しても、端末に書いた値は消えない。
      </Typography.Text>
    </>
  );
}

/** 対象を選ぶダイアログ。 */
export function TargetEditor({
  target,
  onChange,
  ...fields
}: { target: RegistryTarget } & Omit<TargetFieldsProps, "value" | "onChange"> & {
    onChange: (t: RegistryTarget) => void;
  }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<RegistryTarget>(target);
  return (
    <>
      <Button
        size="small"
        onClick={() => {
          setDraft(target);
          setOpen(true);
        }}
      >
        対象…
      </Button>
      <Modal
        title="配る対象"
        open={open}
        okText="決める"
        cancelText="やめる"
        onCancel={() => setOpen(false)}
        onOk={() => {
          onChange(draft);
          setOpen(false);
        }}
        destroyOnClose
      >
        <Form layout="vertical">
          <TargetFields value={draft} onChange={setDraft} {...fields} />
        </Form>
      </Modal>
    </>
  );
}

/** 端末詳細の節: この端末に今当たるレジストリポリシーと、当たる理由。 */
export function DeviceRegistryPolicies({ deviceId }: { deviceId: number }) {
  const api = useApi();
  const groups = useDynGroups();
  const query = useQuery({
    queryKey: ["mdm", "device-registry-policies", deviceId],
    queryFn: () => api.deviceRegistryPolicies(deviceId),
  });
  const groupNames = useMemo(
    () => Object.fromEntries((groups.data ?? []).map((g) => [String(g.id), g.name])),
    [groups.data],
  );
  if (query.isError) {
    return <Alert type="error" showIcon message="取得できなかった" description={errorText(query.error)} />;
  }
  return (
    <Table<DeviceRegistryPolicy>
      scroll={{ x: "max-content" }}
      size="small"
      rowKey={(r) => `${r.hive}\\${r.path}\\${r.name}`}
      loading={query.isLoading}
      dataSource={query.data ?? []}
      pagination={false}
      locale={{ emptyText: "この端末に当たるポリシーは無い" }}
      columns={[
        {
          title: "キー",
          width: 400,
          ellipsis: true,
          onCell: (row) => ({ title: [row.hive, row.path].join("\\") }),
          key: "k",
          render: (_, r) => (
            <Space size={4}>
              <Typography.Text code style={{ fontSize: 12 }}>
                {r.hive}\{r.path}
              </Typography.Text>
              {usersLabel(r) && <Tag>{usersLabel(r)}</Tag>}
            </Space>
          ),
        },
        { title: "名前", width: 160, dataIndex: "name" },
        { title: "値", width: 320, ellipsis: true, onCell: (row) => ({ title: `${String(row.value)}(${row.type})` }), key: "v", render: (_, r) => `${String(r.value)}(${r.type})` },
        {
          title: "当たる理由",
          width: 320,
          ellipsis: true,
          onCell: (row) => ({ title: reasonLabel(row.reason, groupNames) }),
          dataIndex: "reason",
          render: (v: string) => <Tag color="green">{reasonLabel(v, groupNames)}</Tag>,
        },
      ]}
    />
  );
}
